export interface CharacterProfile {
  id: string;               // character_id from官方 or fallback videoId
  displayName: string;      // 用户自定义名称（仅展示，不参与 API）
  sourceVideoId?: string;   // 用于回溯
  sourceVideoUrl?: string;
  coverUrl?: string;        // 角色封面
  avatarUrl?: string;       // 圆形头像
  sourcePrompt?: string;    // 🔥 新增：创建该角色时的原始提示词
  createdAt: string;
}

const STORAGE_KEY = 'character_library_v1';
const DISPLAY_SEQ_KEY = 'character_library_display_seq_v1';
const CHAR_ID_PATTERN = /^char_[a-z0-9]+$/i;
const LEGACY_NUMERIC_PATTERN = /^[0-9]+$/;

const isPlaceholderDisplayName = (name: any) => {
  const v = (name || '').toString().trim();
  if (!v) return true;
  if (v === '未命名角色') return true;
  if (/^新角色-\d{8}$/.test(v)) return true;
  return false;
};

const sanitizeIncomingDisplayName = (name: any) => {
  const v = (name || '').toString().trim();
  return isPlaceholderDisplayName(v) ? '' : v;
};

const readDisplaySeq = () => {
  try {
    const raw = localStorage.getItem(DISPLAY_SEQ_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
};

const writeDisplaySeq = (n: number) => {
  try {
    localStorage.setItem(DISPLAY_SEQ_KEY, String(Math.max(0, Math.floor(n || 0))));
  } catch {}
};

const allocateFriendlyDefaultName = (existingNames: Set<string>) => {
  let seq = readDisplaySeq();
  for (let guard = 0; guard < 9999; guard += 1) {
    seq += 1;
    const candidate = `角色${seq}`;
    if (!existingNames.has(candidate)) {
      writeDisplaySeq(seq);
      return candidate;
    }
  }
  // 极端兜底：不阻塞
  const fallback = `角色${Date.now()}`;
  return existingNames.has(fallback) ? `角色${Date.now()}_${Math.random().toString(16).slice(2, 6)}` : fallback;
};

export const normalizeCharacterId = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(/^@/, '').toLowerCase();
  if (CHAR_ID_PATTERN.test(cleaned)) return cleaned;
  if (/^char[a-z0-9]+$/i.test(cleaned)) {
    const suffix = cleaned.replace(/^char/i, '');
    if (suffix && /^[a-z0-9]+$/i.test(suffix)) {
      return `char_${suffix.toLowerCase()}`;
    }
  }
  if (LEGACY_NUMERIC_PATTERN.test(cleaned)) {
    return `char_${cleaned}`;
  }
  return null;
};

export const requireCharacterId = (raw: string, context = '角色ID'): string => {
  const normalized = normalizeCharacterId(raw);
  if (!normalized) {
    throw new Error(`${context} 必须为 char_xxxx 格式`);
  }
  return normalized;
};

const safeParse = (raw: string | null): any[] => {

  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr;
    return [];
  } catch {
    return [];
  }
};

const persist = (list: CharacterProfile[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const fallbackName = (existingNames: Set<string>) => allocateFriendlyDefaultName(existingNames);

export const characterStore = {
  load(): CharacterProfile[] {
    const rawList = safeParse(localStorage.getItem(STORAGE_KEY));
    const existingNames = new Set<string>();
    (rawList || []).forEach((item: any) => {
      const v = sanitizeIncomingDisplayName(item?.displayName);
      if (v) existingNames.add(v);
    });

    let changed = false;
    const result = (rawList || [])
      .map((item: any) => {
        const normalizedId = normalizeCharacterId(item?.id || item?.character_id || item?.sourceVideoId || '');
        if (!normalizedId) return null;
        let displayName = sanitizeIncomingDisplayName(item?.displayName);
        if (!displayName) {
          displayName = fallbackName(existingNames);
          existingNames.add(displayName);
          changed = true;
        }
        return {
          ...item,
          id: normalizedId,
          displayName,
        } as CharacterProfile;
      })
      .filter(Boolean) as CharacterProfile[];

    if (changed) persist(result);
    return result;
  },


  upsert(profile: CharacterProfile) {
    const normalizedId = requireCharacterId(profile.id, '角色ID');
    const list = this.load();
    const idx = list.findIndex(p => p.id === normalizedId);
    const incomingName = sanitizeIncomingDisplayName(profile.displayName);
    const existingNames = new Set((list || []).map((p) => (p?.displayName || '').toString().trim()).filter(Boolean));
    if (idx >= 0) {
      const nextName = incomingName || list[idx].displayName || fallbackName(existingNames);
      list[idx] = { ...list[idx], ...profile, id: normalizedId, displayName: nextName };
    } else {
      const nextName = incomingName || fallbackName(existingNames);
      list.unshift({ ...profile, id: normalizedId, displayName: nextName } as CharacterProfile);
    }
    persist(list);
    return list;
  },


  update(id: string, patch: Partial<CharacterProfile>) {
    const normalizedId = normalizeCharacterId(id);
    const list = this.load();
    if (!normalizedId) return list;
    const idx = list.findIndex(p => p.id === normalizedId);
    if (idx >= 0) {
      const nextDisplayName = sanitizeIncomingDisplayName(patch.displayName);
      const existingNames = new Set((list || []).map((p) => (p?.displayName || '').toString().trim()).filter(Boolean));
      list[idx] = {
        ...list[idx],
        ...patch,
        ...(nextDisplayName ? { displayName: nextDisplayName } : { displayName: list[idx].displayName || fallbackName(existingNames) }),
      };
      persist(list);
    } else {
      const nextDisplayName = sanitizeIncomingDisplayName(patch.displayName);
      // 允许对“仅来自后端 API 的角色”做前端命名，并持久化到本地库（不影响后端）
      if (nextDisplayName) {
        list.unshift({
          id: normalizedId,
          displayName: nextDisplayName,
          createdAt: new Date().toISOString(),
        } as CharacterProfile);
        persist(list);
      }
    }
    return list;
  },


  remove(id: string) {
    const normalizedId = normalizeCharacterId(id);
    if (!normalizedId) return this.load();
    const list = this.load().filter(p => p.id !== normalizedId);
    persist(list);
    return list;
  },

};

// 统一入口：优先用用户自定义名；否则为该角色分配稳定默认名（角色1/角色2…），并写入本地库
export const getOrCreateCharacterDisplayName = (rawId: string, preferredName?: any): string => {
  const normalizedId = normalizeCharacterId(rawId);
  const preferred = sanitizeIncomingDisplayName(preferredName);
  if (!normalizedId) return preferred || '';

  const list = characterStore.load();
  const existing = list.find((p) => p.id === normalizedId);
  if (existing && !isPlaceholderDisplayName(existing.displayName)) {
    if (preferred && preferred !== existing.displayName) {
      characterStore.update(normalizedId, { displayName: preferred });
      return preferred;
    }
    return existing.displayName;
  }

  const existingNames = new Set((list || []).map((p) => (p?.displayName || '').toString().trim()).filter(Boolean));
  const nextName = preferred || allocateFriendlyDefaultName(existingNames);
  characterStore.upsert({
    id: normalizedId,
    displayName: nextName,
    createdAt: new Date().toISOString(),
  } as CharacterProfile);
  return nextName;
};
